import { useEffect, useState } from 'react'
import './user-list-container.styles.scss'
import { format } from 'date-fns';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { listenToCollection, listenToDoc } from '@/utils/getDataFromDb';
import { phaseLabel, recordText } from '@/utils/leagueLabels';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import UserAvatar from './user-avatar.component';

// One user: their profile and every league they manage a team in.
const UserDetail = () => {

    const { appId } = useParams();
    const [ user, setUser ] = useState(undefined);
    const [ leagues, setLeagues ] = useState(null);
    const [ teams, setTeams ] = useState({});

    useEffect(() => {
        let userListener;
        let leagueListener;
        listenToDoc({
            'path': `users/${appId}`,
            'callback': setUser,
            'handleListener': (unsub) => { userListener = unsub; },
        });
        listenToCollection({
            'path': 'leagues',
            'callback': setLeagues,
            'handleListener': (unsub) => { leagueListener = unsub; },
        });
        return () => {
            if (userListener) userListener();
            if (leagueListener) leagueListener();
        }
    }, [appId])

    const mine = leagues && Object.values(leagues)
        .filter(l => l.settings && l.managers?.[appId])
        .sort((a, b) => (b.ts_created ?? 0) - (a.ts_created ?? 0));
    const leagueKey = (mine ?? []).map(l => l.id).join(',');

    // The team summaries of those leagues, for team names and records.
    useEffect(() => {
        const listeners = [];
        for (const leagueId of leagueKey ? leagueKey.split(',') : []) {
            listenToDoc({
                'path': `leagues/${leagueId}/summary/teams`,
                'callback': (d) => setTeams(prev => ({ ...prev, [leagueId]: d?.teams ?? {} })),
                'handleListener': (unsub) => listeners.push(unsub),
            });
        }
        return () => listeners.forEach(unsub => unsub());
    }, [leagueKey])

    if (user === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;

    return (
        <div className='user-list user-detail'>
            <Link to='/admin/users' className='back-link'><FontAwesomeIcon icon={faArrowLeft} /> All users</Link>
            {!user ?
                <div className='g-card'><p>No profile found for this user.</p></div> :
                <div className='g-card user-profile'>
                    <UserAvatar user={user} size={64} />
                    <div className='user-card-main'>
                        <h2 className='user-name'>{user.name || 'No name yet'}</h2>
                        <a className='user-email' href={`mailto:${user.email}`}>{user.email}</a>
                        <div className='meta'>
                            {user.created > 86400000 ? `Joined ${format(new Date(user.created), 'PP')}` : 'Join date unknown'}
                            {user.status && user.status !== 'active' ? ` · ${user.status}` : ''}
                        </div>
                        <div className='meta app-id'>App ID: {appId}</div>
                    </div>
                </div>
            }

            <h3 className='section-head'>Leagues</h3>
            {!mine && <div className='center-col'><ThreeDotsLoader /></div>}
            {mine && mine.length === 0 && <div className='g-card'><p className='meta'>Not managing a team in any league.</p></div>}
            <div className='user-cards'>
                {mine && mine.map(l => {
                    const teamId = l.managers[appId];
                    const team = teams[l.id]?.[teamId];
                    return (
                        <Link key={l.id} to={`/admin/leagues/${l.id}/teams/${teamId}`} className='g-card user-card league-row'>
                            {team ? <TeamBadge team={team} /> : <span className='badge-space' />}
                            <div className='user-card-main'>
                                <div className='user-name'>{team ? `${team.name} ${team.mascot ?? ''}`.trim() : 'Loading team…'}</div>
                                <div className='league-line'>{l.name}</div>
                                <div className='meta'>
                                    {l.settings.type === 'pro' ? 'Pro' : 'College'} · {phaseLabel(l.season, l.settings.type)}
                                    {team?.record ? ` · ${recordText(team.record)}` : ''}
                                </div>
                            </div>
                            <FontAwesomeIcon icon={faChevronRight} />
                        </Link>
                    )
                })}
            </div>
        </div>
    )
}

export default UserDetail
